<template>
  <div class="page">
    <AppHeader />

    <main class="category-page">
      <div class="category-header">
        <p class="eyebrow">{{ searchTerm ? 'Search' : 'Shop' }}</p>
        <h1 class="category-title">{{ title }}</h1>
        <p v-if="blurb" class="category-subtitle">{{ blurb }}</p>
      </div>

      <p v-if="loading" class="category-status">Loading…</p>
      <ProductGrid
        v-else-if="products.length"
        :products="products"
        :fill="handle === 'new-arrivals' ? 8 : 0"
      />
      <template v-else>
        <p class="category-status">
          {{ searchTerm ? `Nothing matches “${searchTerm}” just yet.` : 'New pieces are on their way. Check back soon.' }}
        </p>
        <PlaceholderGrid v-if="!searchTerm" :count="8" />
      </template>
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import { fetchProducts, fetchCollection } from '../shopify/products'
import { COLLECTIONS } from '../navigation'
import AppHeader from '../components/AppHeader.vue'
import ProductGrid from '../components/ProductGrid.vue'
import PlaceholderGrid from '../components/PlaceholderGrid.vue'
import SiteFooter from '../components/home/SiteFooter.vue'

export default {
  name: 'CategoryPage',

  components: { AppHeader, ProductGrid, PlaceholderGrid, SiteFooter },

  props: {
    // Collection handle; omitted on /shop, which lists everything.
    handle: { type: String, default: null },
  },

  data() {
    return {
      products: [],
      collection: null,
      loading: true,
    }
  },

  computed: {
    searchTerm() {
      return (this.$route.query.q || '').toString().trim()
    },
    title() {
      if (this.searchTerm) return `“${this.searchTerm}”`
      if (!this.handle) return 'Shop All'
      return COLLECTIONS[this.handle]?.title || this.collection?.title || 'Collection'
    },
    blurb() {
      if (this.searchTerm) return ''
      if (!this.handle) return 'Original paintings, ceramics and rugs, every one hand-selected.'
      return COLLECTIONS[this.handle]?.blurb || this.collection?.description || ''
    },
  },

  watch: {
    handle: { immediate: true, handler: 'load' },
    searchTerm: 'load',
  },

  methods: {
    async load() {
      this.loading = true
      this.products = []
      this.collection = null
      try {
        if (this.handle === 'new-arrivals') {
          // Not a Shopify collection: the newest products across the whole
          // store, same as the homepage New Arrivals section.
          this.products = await fetchProducts(24)
        } else if (this.handle) {
          this.collection = await fetchCollection(this.handle)
          this.products = this.collection?.products || []
        } else {
          this.products = await fetchProducts(48, this.searchTerm || null)
        }
      } catch {
        // Missing store config or collection: fall through to the empty state.
        this.products = []
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--canvas);
  color: var(--ink);
}

.category-page {
  max-width: var(--max-w);
  margin: 0 auto;
  padding: 6.4rem 4rem 10rem;
}

.category-header {
  max-width: 64rem;
  margin-bottom: 4.8rem;
}

.category-title {
  font-size: clamp(4rem, 5vw, 6rem);
  font-weight: 400;
  line-height: 1.05;
  margin: 1rem 0 1.4rem;
}

.category-subtitle {
  font-size: 1.6rem;
  font-weight: 300;
  line-height: 1.65;
  color: var(--charcoal);
}

.category-status {
  font-size: 1.5rem;
  color: var(--stone);
  margin-bottom: 3.2rem;
}

@media (max-width: 960px) {
  .category-page { padding: 4rem 1.6rem 6.4rem; }
}
</style>
